import {useEffect, useRef, useState} from 'react';

const openDialogs = [];
let priorOverflow = '';
let originalOpener = null;

export function useReducedMotion() {
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(query.matches);
    if (query.addEventListener) {
      query.addEventListener('change', update);
      return () => query.removeEventListener('change', update);
    }
    query.addListener(update);
    return () => query.removeListener(update);
  }, []);
  return reduced;
}

export function useDialogFocus({open, dialogRef, onClose, busy = false}) {
  const closeRef = useRef(onClose);
  const busyRef = useRef(busy);
  closeRef.current = onClose;
  busyRef.current = busy;
  useEffect(() => {
    if (!open || !dialogRef.current) return undefined;
    const previous = document.activeElement;
    const dialog = dialogRef.current;
    const entry = Symbol('dialog');
    if (!openDialogs.length) {
      originalOpener = previous;
      priorOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    openDialogs.push(entry);
    const isTop = () => openDialogs[openDialogs.length - 1] === entry;
    const focusables = () => Array.from(dialog.querySelectorAll('button,a[href],input,select,textarea,[tabindex]'))
      .filter(el => !el.disabled && el.tabIndex >= 0 && el.getClientRects().length > 0 && !el.closest('[aria-hidden="true"],[hidden],[inert]'));
    const available = focusables();
    const initial = available.find(el=>el.hasAttribute('data-autofocus')) || available[0] || dialog;
    initial.focus({preventScroll: true});
    const focusin = event => {
      if (isTop() && !dialog.contains(event.target)) (focusables()[0] || dialog).focus({preventScroll: true});
    };
    const keydown = event => {
      if (!isTop()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!busyRef.current) closeRef.current?.();
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) { event.preventDefault(); dialog.focus(); return; }
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', keydown);
    document.addEventListener('focusin', focusin);
    return () => {
      const wasTop = isTop();
      document.removeEventListener('keydown', keydown);
      document.removeEventListener('focusin', focusin);
      const index = openDialogs.indexOf(entry);
      if (index !== -1) openDialogs.splice(index, 1);
      if (!openDialogs.length) document.body.style.overflow = priorOverflow;
      const target = previous?.isConnected ? previous : originalOpener;
      if (wasTop && target?.isConnected && typeof target.focus === 'function') target.focus({preventScroll: true});
      if (!openDialogs.length) originalOpener = null;
    };
  }, [open, dialogRef]);
}
